import { ArrowDownRight, BadgeCheck, CircleAlert, Scale } from "lucide-react";
import type { FinancingResult, SimulationInput } from "../../shared/contracts";
import { currency, currencyDetailed, systemLabel } from "../lib/format";

interface DecisionSummaryProps {
  best: FinancingResult;
  input: SimulationInput;
  eligibleCount: number;
  totalCount: number;
  onLead: () => void;
}

export function DecisionSummary({
  best,
  input,
  eligibleCount,
  totalCount,
  onLead
}: DecisionSummaryProps) {
  const financedAmount = input.propertyValue - input.downPayment;
  const economyVsFinanced = best.totalPaid - financedAmount;

  return (
    <section className="decision-summary" aria-labelledby="decision-title">
      <div className="decision-summary__topline">
        <span className="section-kicker section-kicker--light">02 · Leitura rápida</span>
        <span className="live-indicator">
          <i aria-hidden="true" />
          cálculo atualizado
        </span>
      </div>

      <div className="decision-summary__content">
        <div>
          <p className="decision-label">
            {best.eligibility.eligible
              ? "Menor custo elegível nesta simulação"
              : "Menor custo entre as opções que exigem revisão"}
          </p>
          <h1 id="decision-title">
            {best.bankShortName}
            <span>{systemLabel(best.system)}</span>
          </h1>
          <p className="decision-copy">
            A primeira parcela consome{" "}
            <strong>{best.eligibility.incomeUsagePct.toFixed(1).replace(".", ",")}%</strong> da
            renda informada.{" "}
            {best.eligibility.eligible
              ? "O custo contempla juros, seguro ilustrativo, tarifa mensal e tarifa inicial configurada."
              : "Nenhuma opção atende a todos os critérios; ajuste a entrada, renda ou prazo para revisar o enquadramento."}
          </p>
        </div>

        <div className="decision-amount">
          <span>Primeira parcela estimada</span>
          <strong>{currencyDetailed(best.firstPayment)}</strong>
          <small>
            <ArrowDownRight size={15} aria-hidden="true" />
            termina em {currencyDetailed(best.lastPayment)}
          </small>
        </div>
      </div>

      <div className="decision-stats">
        <div>
          <Scale size={18} aria-hidden="true" />
          <span>
            <strong>
              {eligibleCount}/{totalCount}
            </strong>
            opções cabem nos critérios
          </span>
        </div>
        <div>
          <BadgeCheck size={18} aria-hidden="true" />
          <span>
            <strong>{best.annualRate.toFixed(2).replace(".", ",")}% a.a.</strong>
            taxa efetiva ilustrativa
          </span>
        </div>
        <div>
          <CircleAlert size={18} aria-hidden="true" />
          <span>
            <strong>{currency(economyVsFinanced)}</strong>
            encargos totais estimados
          </span>
        </div>
        <button className="button button--paper" type="button" onClick={onLead}>
          Receber estudo completo
        </button>
      </div>
    </section>
  );
}
